import React from "react";
import { EnvelopeIcon } from "@heroicons/react/24/solid";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";

import { dateFormatter } from "../../../../utils/dateFormatter";

import { FormInput } from "../../../shared/formElements/formInput/FormInput";
import { FormCheckboxChips } from "../../../shared/formElements/formCheckBoxChip/FormCheckBoxChip";

import { FormDynamicKeyValueList } from "../../../shared/formElements/formDynamicKeyValueList/FormDynamicKeyValueList";

import classes from "./bulkSpareDetailForm.module.css";
import { ChatPanel } from "../../../chatPanel/ChatPanel";
import { useNavigate } from "react-router-dom";
import { FormDropdown } from "../../../shared/formElements/formDropdown/FormDropdown";
import { FormTextarea } from "../../../shared/formElements/formTextarea/FormTextarea";
import { FormDynamicStringList } from "../../../shared/formElements/formDynamicStringList/FormDynamicStringList";

export const StatusBadge = ({ status }) => {
  const statusConfig = {
    approved: {
      label: "Approved",
      bg: "#E6F7EF",
      color: "#00A167",
      Icon: CheckCircleIcon,
    },
    pending: {
      label: "Pending",
      bg: "#FFF4E5",
      color: "#FF8C00",
      Icon: ClockIcon,
    },
    rejected: {
      label: "Rejected",
      bg: "#FDECEC",
      color: "#E53935",
      Icon: XCircleIcon,
    },
  };

  const config = statusConfig?.[status?.toLowerCase()];
  if (!config) return null;

  const { label, bg, color, Icon } = config;

  return (
    <div className={classes.status}>
      <h4 className={classes.status__title}>Status:</h4>
      <div
        className={classes.status__badge}
        style={{
          backgroundColor: bg,
          color,
        }}
      >
        <Icon style={{ width: 14, height: 14 }} />
        {label}
      </div>
    </div>
  );
};

export const BulkSpareDetailForm = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
}) => {
  /* =========================
     FORM SETUP
  ========================= */
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { isValid, errors,dirtyFields },
  } = useForm({
    defaultValues: {
      ...initialValues,
      features:
        initialValues?.features?.length > 0
          ? initialValues.features
          : [{ name: "", value: "" }],
    },
    mode: "onChange",
  });

  /* =========================
     SUBMIT HANDLER
  ========================= */

   const hasChanges = Object.keys(dirtyFields).length > 0;

  const submitHandler = (data) => {
    if (!onSubmit) return;
    onSubmit(data);
  };

  /* =========================
     RENDER
  ========================= */
  console.log(initialValues);

  const handleNavigateToList = () => {
    navigate(`/bulk_deals/spares/${initialValues?.request_id}/items`);
  };

  return (
    <div className={classes.box}>
      <div className={classes.box__lot}>
        <form
          className={classes.box__form}
          onSubmit={handleSubmit(submitHandler)}
        >
          {/* =========================
             LOT INFORMATION
          ========================= */}

          <div className={`${classes.box__section} ${classes.box__lot__info}`}>
            <div className={classes.box__section__head}>
              <h3 className={classes.box__section__head__title}>
                Lot Information
              </h3>

              <div className={classes.box__section__action}>
                <button
                  type="button"
                  className={classes.box__lot__action__btn}
                  onClick={handleNavigateToList}
                >
                  View Item list
                </button>
              </div>
            </div>

            <div className={classes.box__grid}>
              <FormInput
                id="request_id"
                label="Lot Request Id"
                register={register}
                validation={{
                  required: "Request Id is required",
                }}
                error={errors.request_id?.message}
                required
                disabled
              />
              <FormInput
                id="lot_name"
                label="Lot Name"
                register={register}
                validation={{
                  required: "Lot Name is required",
                }}
                error={errors.lot_name?.message}
                required
              />
              <FormInput
                id="lot_price"
                label="Price"
                register={register}
                validation={{
                  required: "Request Id is required",
                }}
                error={errors.lot_price?.message}
                required
                disabled
              />
              <FormInput
                id="quantity"
                label="Quantity"
                register={register}
                validation={{
                  required: "Request Id is required",
                }}
                error={errors.quantity?.message}
                required
                disabled
              />
              <FormInput
                id="city"
                label="City"
                register={register}
                validation={{
                  required: "City is required",
                }}
                error={errors.city?.message}
                required
              />
              <FormInput
                id="state"
                label="State"
                register={register}
                validation={{
                  required: "State is required",
                }}
                error={errors.state?.message}
                required
              />

              <FormCheckboxChips
                id="status"
                label="Status"
                floatingLabel
                options={[
                  { id: "active", label: "Active" },
                  { id: "inactive", label: "Inactive" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.status?.message}
                required
              />
              <FormCheckboxChips
                id="sale_type"
                label="Sale Type"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "Featured", label: "Featured" },
                  { id: "Normal", label: "Normal" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.sale_type?.message}
                required
              />
              <FormCheckboxChips
                id="stock_status"
                label="Stock Status"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "in stock", label: "In Stock" },
                  { id: "out of stock", label: "Out Of Stock" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.sale_type?.message}
                required
              />
              <FormCheckboxChips
                id="inspection_available"
                label="Inspection"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "yes", label: "Yes" },
                  { id: "no", label: "No" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.sale_type?.message}
                required
              />

              <FormCheckboxChips
                id="unit_type"
                label="Unit Type"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "pieces", label: "Pieces" },
                  { id: "kg", label: "Kg" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.status?.message}
                required
              />
            </div>
          </div>

          {/* <div className={`${classes.box__section} ${classes.box__lot__info}`}>
            <div className={classes.box__section__head}>
              <h3 className={classes.box__section__head__title}>Location</h3>
            </div>

            <div className={classes.box__grid}>
              <FormInput
                id="city"
                label="City"
                register={register}
                validation={{
                  required: "City is required",
                }}
                error={errors.city?.message}
                required
              />
              <FormInput
                id="state"
                label="State"
                register={register}
                validation={{
                  required: "State is required",
                }}
                error={errors.state?.message}
                required
              />
            </div>
          </div> */}

          {/* <div className={`${classes.box__section} ${classes.box__lot__info}`}>
            <div className={classes.box__section__head}>
              <h3 className={classes.box__section__head__title}>
                Sale & Stock
              </h3>
            </div>

            <div className={classes.box__grid}>
              <FormCheckboxChips
                id="sale_type"
                label="Sale Type"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "Featured", label: "Featured" },
                  { id: "Normal", label: "Normal" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.sale_type?.message}
                required
              />
              <FormCheckboxChips
                id="stock_status"
                label="Stock Status"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "in stock", label: "In Stock" },
                  { id: "out of stock", label: "Out Of Stock" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.sale_type?.message}
                required
              />
              <FormCheckboxChips
                id="inspection_available"
                label="Inspection"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "yes", label: "Yes" },
                  { id: "no", label: "No" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.sale_type?.message}
                required
              />
            </div>
          </div> */}
          {/* <div className={`${classes.box__section} ${classes.box__lot__info}`}>
            <div className={classes.box__section__head}>
              <h3 className={classes.box__section__head__title}>
                Pricing & Quantity
              </h3>
            </div>

            <div className={classes.box__grid}>
              <FormInput
                id="lot_price"
                label="Price"
                register={register}
                validation={{
                  required: "Request Id is required",
                }}
                error={errors.lot_price?.message}
                required
                disabled
              />
              <FormInput
                id="quantity"
                label="Quantity"
                register={register}
                validation={{
                  required: "Request Id is required",
                }}
                error={errors.quantity?.message}
                required
                disabled
              />
              <FormCheckboxChips
                id="unit_type"
                label="Unit Type"
                floatingLabel // optional (remove if you don’t want floating)
                options={[
                  { id: "pieces", label: "Pieces" },
                  { id: "kg", label: "Kg" },
                ]}
                watch={watch}
                setValue={setValue}
                register={register}
                validation={{ required: "Status is required" }}
                error={errors.status?.message}
                required
              />
            </div>
          </div> */}

          <div className={`${classes.box__section} ${classes.box__feature}`}>
            <h3 className={classes.box__section__head__title}>Lot Features</h3>
            {/* <FormDynamicKeyValueList
              control={control}
              register={register}
              setValue={setValue}
              fieldName="features"
              errors={errors?.features}
              required
              layout="stacked"
            /> */}
            <FormDynamicStringList
              control={control}
              register={register}
              setValue={setValue}
              fieldName="features"
              errors={errors?.features}
              uniqueKey="name"
              valueKey="value"
              required
            />
          </div>

          <div className={`${classes.box__section} ${classes.box__feature}`}>
            <h3 className={classes.box__section__head__title}>Comments</h3>
            <FormTextarea
              id="comments"
              label="Comments"
              register={register}
              validation={{
                required: "Comments is required",
                minLength: {
                  value: 10,
                  message: "Minimum 10 characters required",
                },
              }}
              required
            />
          </div>
           <button
            type="submit"
            className={classes.box__form__submitBtn}
            // disabled={!isValid || !hasChanges || isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Lot"}
          </button>
        </form>
      </div>
    </div>
  );
};
